import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import type { TDesignSystem } from "@/shared/types";
import { designSystemApi } from "./design-system.api";
import { designSystemKeys } from "./design-system.keys";

export const useCreateThemeMutation = () => {
	return useMutation({
		mutationFn: (data: {
			payload: TDesignSystem["designSystem"];
			title: TDesignSystem["title"];
			customColors?: TDesignSystem["customColors"];
		}) => designSystemApi.createTheme(data),
		onSuccess: () => {
			reactQueryUtils.invalidateQueries([...designSystemKeys.themes]);
		},
	});
};

export const useUpdateThemeMutation = () => {
	return useMutation({
		mutationFn: (data: {
			id: string;
			payload: TDesignSystem["designSystem"];
			title: TDesignSystem["title"];
			customColors?: TDesignSystem["customColors"];
		}) => designSystemApi.updateTheme(data),
		onMutate: async (variables) => {
			await queryClient.cancelQueries({
				queryKey: designSystemKeys.theme(variables.id),
			});
			await queryClient.cancelQueries({ queryKey: designSystemKeys.themes });

			const previousTheme = reactQueryUtils.getQueryData<TDesignSystem>([
				...designSystemKeys.theme(variables.id),
			]);
			const previousThemes = reactQueryUtils.getQueryData<TDesignSystem[]>([
				...designSystemKeys.themes,
			]);

			if (previousTheme) {
				const optimisticTheme = {
					...previousTheme,
					designSystem: variables.payload,
					title: variables.title,
					...(variables.customColors !== undefined && {
						customColors: variables.customColors,
					}),
				};
				reactQueryUtils.setQueryData(
					[...designSystemKeys.theme(variables.id)],
					optimisticTheme,
				);
			}

			if (previousThemes) {
				const optimisticThemes = previousThemes.map((theme) =>
					theme._id === variables.id
						? {
								...theme,
								designSystem: variables.payload,
								title: variables.title,
								...(variables.customColors !== undefined && {
									customColors: variables.customColors,
								}),
							}
						: theme,
				);
				reactQueryUtils.setQueryData(
					[...designSystemKeys.themes],
					optimisticThemes,
				);
			}

			return { previousTheme, previousThemes };
		},
		onError: (_error, variables, context) => {
			if (context?.previousTheme) {
				reactQueryUtils.setQueryData(
					[...designSystemKeys.theme(variables.id)],
					context.previousTheme,
				);
			}
			if (context?.previousThemes) {
				reactQueryUtils.setQueryData(
					[...designSystemKeys.themes],
					context.previousThemes,
				);
			}
		},
		onSettled: (_, __, variables) => {
			reactQueryUtils.invalidateQueries([
				...designSystemKeys.theme(variables.id),
			]);
			reactQueryUtils.invalidateQueries([...designSystemKeys.themes]);
		},
	});
};

export const useDeleteThemeMutation = () => {
	return useMutation({
		mutationFn: (id: string) => designSystemApi.deleteTheme(id),
		onSuccess: () => {
			reactQueryUtils.invalidateQueries([...designSystemKeys.themes]);
		},
	});
};
